import { p as proxyCustomElement, H, h, F as Fragment, d as Host } from './p-bfa0dc67.js';
import { c as classNames } from './p-df8c8ec2.js';
import { i as inheritAriaAttributes } from './p-198613ef.js';
import { d as defineCustomElement$1 } from './p-ee598a15.js';

/**
 * Sets attributes on an HTMLElement.
 *
 * @param {HTMLElement} host - The target HTMLElement to set attributes on.
 * @param {{ [x: string]: string }} attributes - An object of attribute names and their corresponding values.
 * @returns {void}
 */
const setAttributes = (host, attributes) => {
    for (const args of Object.entries(attributes)) {
        host.setAttribute(...args);
    }
};
// Copy of https://github.com/porsche-design-system/porsche-design-system/blob/9c42c339334f276d6f3030015266a006a6fc386c/packages/components/src/utils/button-handling.ts
const improveButtonHandlingForCustomElement = (element, getType, getDisabled, getName, getValue) => {
    element.addEventListener('click', (event) => handleButtonEvent(event, element, getType, getDisabled, getName, getValue));
};
const handleButtonEvent = (event, element, getType, getDisabled, getName, getValue) => {
    // Why? That's why: https://hjorthhansen.dev/shadow-dom-and-forms/
    const form = element.closest('form');
    if (form && !getDisabled()) {
        /**
         * we've to wait if someone calls preventDefault on the event
         * then we shouldn't submit the form
         */
        window.setTimeout(() => {
            if (!event.defaultPrevented) {
                const name = getName?.();
                const value = getValue?.();
                const fakeButton = document.createElement('button');
                setAttributes(fakeButton, {
                    ...(name && { name }),
                    ...(value && { value }),
                    type: getType(),
                });
                fakeButton.style.display = 'none';
                form.appendChild(fakeButton);
                fakeButton.addEventListener('click', (fakeButtonEvent) => {
                    fakeButtonEvent.stopPropagation();
                });
                fakeButton.click();
                fakeButton.remove();
            }
        }, 1);
    }
};

const biButtonCss = ".sr-only{position:absolute;height:1px;width:1px;padding:0;margin:-1px;overflow:hidden;border:0;left:-9999px;text-indent:-5000px;clip:rect(1px, 1px, 1px, 1px);}*,*:before,*:after{box-sizing:border-box}html,body,div,span,object,iframe,figure,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,code,em,img,small,strike,strong,sub,sup,tt,b,u,i,ol,ul,li,fieldset,form,label,table,caption,tbody,tfoot,thead,tr,th,td,main,canvas,embed,footer,header,nav,section,button,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;text-size-adjust:none}footer,header,nav,section,main{display:block}body{line-height:1.5}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:before,blockquote:after,q:before,q:after{content:\"\";content:none}table{border-collapse:collapse;border-spacing:0}input{-webkit-appearance:none;appearance:none;border-radius:0}:host(:focus){outline:none !important;border:none !important}:host{display:block;width:fit-content}:host([data-stretch]){width:100%}:host([data-active]){border-radius:var(--bi-radius-medium);box-shadow:0px 0px 0px 1.5px var(--bi-primary-50) inset}button,a{font-family:\"museo-sans\", arial, sans-serif;border-radius:var(--bi-radius-medium);border:none;display:flex;text-decoration:unset;align-items:center;justify-content:center;gap:var(--bi-scale-1x);position:relative;overflow:hidden;cursor:pointer;background:transparent;font-weight:600;--btn-box-shadow:none;box-shadow:var(--btn-box-shadow);width:100%}button:focus-visible,a:focus-visible{outline-offset:2px;outline:1.5px solid var(--bi-primary-50);box-shadow:0 0 0 4.5px var(--bi-basic-white), var(--btn-box-shadow)}button[data-size=small],a[data-size=small]{font-size:0.875rem;line-height:1.25rem;min-height:var(--bi-scale-8x);padding:var(--bi-scale-1-5x) var(--bi-scale-3x)}button[data-size=small] ::slotted([slot=icon-left]),button[data-size=small] ::slotted([slot=icon-right]),a[data-size=small] ::slotted([slot=icon-left]),a[data-size=small] ::slotted([slot=icon-right]){font-size:var(--bi-scale-4x)}button[data-size=small].icon-only,a[data-size=small].icon-only{height:var(--bi-scale-8x);padding:var(--bi-scale-1-5x);font-size:var(--bi-scale-4x)}button[data-size=medium],a[data-size=medium]{font-size:1rem;line-height:1.5rem;min-height:var(--bi-scale-11x);padding:0.625rem var(--bi-scale-6x)}button[data-size=medium] ::slotted([slot=icon-left]),button[data-size=medium] ::slotted([slot=icon-right]),a[data-size=medium] ::slotted([slot=icon-left]),a[data-size=medium] ::slotted([slot=icon-right]){font-size:var(--bi-scale-6x)}button[data-size=medium].icon-only,a[data-size=medium].icon-only{padding:0.625rem;height:var(--bi-scale-11x);font-size:var(--bi-scale-6x)}button[data-size=large],a[data-size=large]{font-size:1rem;line-height:1.5rem;min-height:var(--bi-scale-14x);padding:var(--bi-scale-2x) var(--bi-scale-9x)}button[data-size=large] ::slotted([slot=icon-left]),button[data-size=large] ::slotted([slot=icon-right]),a[data-size=large] ::slotted([slot=icon-left]),a[data-size=large] ::slotted([slot=icon-right]){font-size:var(--bi-scale-8x)}button[data-size=large].icon-only,a[data-size=large].icon-only{height:var(--bi-scale-14x);padding:var(--bi-scale-3x);font-size:var(--bi-scale-8x)}button .button-text,a .button-text{padding:0 var(--bi-scale-1x);text-align:center}button[data-variant=filled],a[data-variant=filled]{--btn-filled-color:var(--bi-basic-white);--btn-filled-bg-color:var(--bi-primary-50);--btn-filled-hover-bg-color:linear-gradient(\n      0deg,\n      var(--bi-states-hover-dark) 0%,\n      var(--bi-states-hover-dark) 100%\n    ),\n    var(--btn-filled-bg-color);--btn-filled-press-bg-color:linear-gradient(\n      0deg,\n      var(--bi-states-press-dark) 0%,\n      var(--bi-states-press-dark) 100%\n    ),\n    var(--btn-filled-bg-color);color:var(--btn-filled-color);background-color:var(--btn-filled-bg-color);--btn-box-shadow:0px 0px 0px 2px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset,\n    0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset, 0px 1px 2px 0px rgba(10, 13, 18, 0.05)}button[data-variant=filled]:not(.disabled):hover,a[data-variant=filled]:not(.disabled):hover{background:var(--btn-filled-hover-bg-color)}button[data-variant=filled]:not(.disabled):active,a[data-variant=filled]:not(.disabled):active{background:var(--btn-filled-press-bg-color)}button[data-variant=outlined],a[data-variant=outlined]{--btn-outlined-color:var(--bi-primary-50);--btn-outlined-box-shadow-color:var(--bi-primary-50);--btn-outlined-hover-bg-color:var(--bi-states-hover-dark);--btn-outlined-press-bg-color:var(--bi-states-press-dark);color:var(--btn-outlined-color);--btn-box-shadow:0px 0px 0px 2px var(--btn-outlined-box-shadow-color) inset}button[data-variant=outlined]:not(.disabled):hover,a[data-variant=outlined]:not(.disabled):hover{background:var(--btn-outlined-hover-bg-color)}button[data-variant=outlined]:not(.disabled):active,a[data-variant=outlined]:not(.disabled):active{background:var(--btn-outlined-press-bg-color)}button[data-variant=outlined].dark,a[data-variant=outlined].dark{--btn-outlined-color:var(--bi-basic-white);--btn-outlined-box-shadow-color:var(--bi-basic-white);--btn-outlined-hover-bg-color:var(--bi-states-hover-light);--btn-outlined-press-bg-color:var(--bi-states-press-light)}button[data-variant=text],a[data-variant=text]{--btn-text-color:var(--bi-neutral-90);--btn-text-hover-bg-color:var(--bi-states-hover-dark);--btn-text-press-bg-color:var(--bi-states-press-dark);color:var(--btn-text-color)}button[data-variant=text]:not(.disabled):hover,a[data-variant=text]:not(.disabled):hover{background:var(--btn-text-hover-bg-color)}button[data-variant=text]:not(.disabled):active,a[data-variant=text]:not(.disabled):active{background:var(--btn-text-press-bg-color)}button[data-variant=text].dark,a[data-variant=text].dark{--btn-text-color:var(--bi-basic-white);--btn-text-hover-bg-color:var(--bi-states-hover-light);--btn-text-press-bg-color:var(--bi-states-press-light)}button[data-variant=flat],a[data-variant=flat]{--btn-flat-color:var(--bi-primary-90);--btn-flat-bg-color:var(--bi-primary-5);--btn-flat-hover-bg-color:linear-gradient(0deg, var(--bi-states-hover-dark) 0%, var(--bi-states-hover-dark) 100%),\n    var(--btn-flat-bg-color);--btn-flat-press-bg-color:linear-gradient(0deg, var(--bi-states-press-dark) 0%, var(--bi-states-press-dark) 100%),\n    var(--btn-flat-bg-color);color:var(--btn-flat-color);background:var(--btn-flat-bg-color)}button[data-variant=flat]:not(.disabled):hover,a[data-variant=flat]:not(.disabled):hover{background:var(--btn-flat-hover-bg-color)}button[data-variant=flat]:not(.disabled):active,a[data-variant=flat]:not(.disabled):active{background:var(--btn-flat-press-bg-color)}button[data-variant=flat].dark,a[data-variant=flat].dark{--btn-flat-color:var(--bi-basic-white);--btn-flat-bg-color:rgba(255, 255, 255, 0.1);--btn-flat-hover-bg-color:linear-gradient(\n      0deg,\n      var(--bi-states-hover-light) 0%,\n      var(--bi-states-hover-light) 100%\n    ),\n    var(--btn-flat-bg-color);--btn-flat-press-bg-color:linear-gradient(\n      0deg,\n      var(--bi-states-press-light) 0%,\n      var(--bi-states-press-light) 100%\n    ),\n    var(--btn-flat-bg-color)}button.disabled,a.disabled{cursor:not-allowed}a.disabled{pointer-events:none}";
const BiButtonStyle0 = biButtonCss;

const BiButton = /*@__PURE__*/ proxyCustomElement(class BiButton extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.internals = this.attachInternals?.();
        this.inheritedAttributes = {};
        /**
         * The type of button.
         * @default 'button'
         */
        this.type = 'button';
        /**
         * The button variant.
         * @default 'filled'
         */
        this.variant = 'filled';
        /**
         * The button size.
         * @default 'medium'
         */
        this.size = 'medium';
        /**
         * Is the component displayed on a dark background.
         * @default false
         */
        this.dark = false;
        /**
         * Display as icon-button, only use when showing an icon as the label.
         * Not compatible with the icon-slots or `stretch`.
         * Always provide a descriptive aria-label for accessibility when set to true.
         * @default false
         */
        this.icon = false;
        /**
         * Decides if the button is disabled.
         * @default false
         */
        this.disabled = false;
        /*
         * Stretch the button to the full width.
         * @default false
         */
        this.stretch = false;
        /**
         * Used for displaying a loading animation, indicating its state.
         * Overrides the "icon-left" slot when used
         * @default false
         */
        this.submitting = false;
        /**
         * Make the button active by showing the focus-visible state.
         * @default false
         */
        this.active = false;
        /**
         * Specifies where to display the linked URL.
         * Only applies when an `href` is provided.
         * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
         */
        this.target = undefined;
    }
    onClick(e) {
        if (this.disabled || this.submitting) {
            e.stopPropagation();
            return;
        }
        if (this.form && this.internals?.form) {
            e.preventDefault();
            if (this.type === 'submit') {
                // Submitter is null because the button can't be passed from the shadow DOM https://github.com/WICG/webcomponents/issues/814
                this.internals?.form.requestSubmit();
            }
            else if (this.type === 'reset') {
                this.internals?.form.reset();
            }
        }
    }
    onValueChange(newValue) {
        if (this.form) {
            this.internals?.setFormValue(newValue);
        }
    }
    componentWillLoad() {
        if (this.form) {
            this.internals?.setFormValue(this.value);
        }
        this.inheritedAttributes = inheritAriaAttributes(this.el);
    }
    componentDidLoad() {
        if (!this.form) {
            improveButtonHandlingForCustomElement(this.el, () => this.type, () => this.disabled || this.submitting, () => this.name, () => this.value);
        }
    }
    render() {
        const { type, target, href } = this;
        const TagType = this.href === undefined ? 'button' : 'a';
        const attrs = TagType === 'button'
            ? { type }
            : {
                href,
                target,
            };
        return (h(Host, { key: 'e17437319de5966657347c3401c03b11f11876f1', "aria-disabled": String(this.disabled), "data-active": this.active, "data-stretch": this.stretch && !this.icon }, h(TagType, { key: 'b7d565936f600192bbb215f46ca9f68142b45b38', ...attrs, name: this.name, disabled: this.disabled, class: classNames(this.disabled && 'disabled', this.icon && 'icon-only', this.dark && 'dark'), "data-variant": this.variant, "data-size": this.size, ...this.inheritedAttributes }, this.icon ? (this.submitting ? (h("bi-spinner", { "aria-hidden": 'true' })) : (h("slot", null))) : (h(Fragment, null, this.submitting ? h("bi-spinner", { "aria-hidden": 'true' }) : h("slot", { name: 'icon-left' }), h("span", { class: 'button-text' }, h("slot", null)), h("slot", { name: 'icon-right' }))))));
    }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get assetsDirs() { return ["../assets"]; }
    get el() { return this; }
    static get watchers() { return {
        "value": ["onValueChange"]
    }; }
    static get style() { return BiButtonStyle0; }
}, [81, "bi-button", {
        "type": [513],
        "name": [513],
        "value": [1],
        "variant": [1],
        "size": [1],
        "dark": [4],
        "icon": [4],
        "disabled": [516],
        "stretch": [4],
        "submitting": [4],
        "active": [4],
        "href": [1],
        "target": [1],
        "form": [513]
    }, [[2, "click", "onClick"]], {
        "value": ["onValueChange"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bi-button", "bi-spinner"];
    components.forEach(tagName => { switch (tagName) {
        case "bi-button":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BiButton);
            }
            break;
        case "bi-spinner":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { BiButton as B, defineCustomElement as d };
